import type { ReactNode } from 'react'
import type { EntityRef, NotificationItem, NotificationItemPayload } from '@/api/generated/models'

// Emphasized entity name inside a notification's rendered text. Plain text, NOT
// a link — the whole notification row is the single click target (navigates to
// item.link); a secondary inline link would be redundant and confusing.
function EntityName({ name }: { name: string }) {
  return <span className="font-medium">{name}</span>
}

// Resolves the notification payload's named int refs (playerId, toTeamId, …)
// to inline text. Unknown ids — and entities whose name is null — degrade to
// neutral plain text — never a crash.
export interface NotificationLookup {
  team(id: number | null | undefined): ReactNode
  player(id: number | null | undefined): ReactNode
  league(id: number | null | undefined): ReactNode
}

export function buildLookup(
  players: EntityRef[],
  teams: EntityRef[],
  leagues: EntityRef[],
): NotificationLookup {
  const playerById = new Map(players.map((p) => [p.id, p]))
  const teamById = new Map(teams.map((t) => [t.id, t]))
  const leagueById = new Map(leagues.map((l) => [l.id, l]))
  return {
    team(id) {
      if (id == null) return <span>a team</span>
      const t = teamById.get(id)
      return t && t.name ? <EntityName name={t.name} /> : <span>another team</span>
    },
    player(id) {
      if (id == null) return <span>a player</span>
      const p = playerById.get(id)
      return p && p.name ? <EntityName name={p.name} /> : <span>a player</span>
    },
    // League-wide notices are sent to owners in that league, so an unresolved
    // id degrades to "your league".
    league(id) {
      if (id == null) return <span>your league</span>
      const l = leagueById.get(id)
      return l && l.name ? <EntityName name={l.name} /> : <span>your league</span>
    },
  }
}

// NotificationItemPayload is generated as `{[key: string]: number} | null` —
// an untyped bag of named int refs, not a per-type shape. Renderers below
// pull the keys they expect (playerId, toTeamId, teamId, …); a missing key
// reads as `undefined`, which the lookup functions already treat as "no id".
type PayloadBag = NonNullable<NotificationItemPayload>
type Renderer = (payload: PayloadBag, lookup: NotificationLookup) => ReactNode

// The per-type registry — the frontend half of the notification registry.
// Add a new entry here when the backend registry gains a type. Unlisted/null
// types fall through to the generic renderer below.
const RENDERERS: Record<string, Renderer> = {
  'trade.offer.received': (p, l) => (
    <>{l.team(p.fromTeamId)} sent you a trade offer.</>
  ),
  'draft.onTheClock': (p, l) => (
    <>The {l.team(p.teamId)} are on the clock.</>
  ),
  'trade.offer.accepted': (p, l) => (
    <>{l.team(p.otherTeamId)} accepted your trade offer.</>
  ),
  'trade.offer.declined': (p, l) => (
    <>{l.team(p.otherTeamId)} declined your trade offer.</>
  ),
  'trade.offer.countered': (p, l) => (
    <>{l.team(p.otherTeamId)} sent a counter to your trade offer.</>
  ),
  'trade.offer.cancelled': (p, l) => (
    <>{l.team(p.otherTeamId)} cancelled a trade offer.</>
  ),
  'draft.listDropped': (p, l) => (
    <>The draft list is ready for {l.team(p.teamId)}.</>
  ),
  // The keep/release nudge is deliberate: the playoffs-started EMAIL carries a
  // whole "Planning for Next Season" section (salary demands, contracts, the
  // hard-cap window), but only ~48% of active owners pass the email opt-in gate
  // — the legacy all-or-nothing `users.leagueemail = 0` opt-out was backfilled
  // into a per-type email preference. The in-app notice reaches everyone, so it
  // has to at least say there's a decision to make.
  'league.playoffsStarted': (p, l) => (
    <>The playoffs have started in {l.league(p.leagueId)} - remember to set your keep/release decisions for next season.</>
  ),
  'league.seasonStart.franchise': (p, l) => (
    <>A new season has started in {l.league(p.leagueId)}.</>
  ),
  'league.seasonStart.rookie': () => (
    <>Your rookie league is ready, prepare your team for Opening Day.</>
  ),
  'league.tradeDeadlineSoon': (p, l) => (
    <>The trade deadline is approaching in {l.league(p.leagueId)}.</>
  ),
  'league.seasonEnding': (p, l) => (
    <>The World Series has ended in {l.league(p.leagueId)}, a new season starts soon.</>
  ),
  'team.movedToExhibition': (p, l) => (
    <>The {l.team(p.teamId)} have moved to an exhibition league.</>
  ),
  // Admin tooling -- an admin broadcast: sent to every active site admin (and emailed to
  // support) when a background unowned-team fill from the Create League tool finishes or
  // fails, never to a league owner.
  'admin.leagueTeamFill.completed': (p, l) => (
    <>The background team fill for {l.league(p.leagueId)} finished — {p.createdCount ?? 0} team(s) created.</>
  ),
  'admin.leagueTeamFill.failed': (p, l) => (
    <>
      The background team fill for {l.league(p.leagueId)} failed after creating {p.createdCount ?? 0} team(s). Re-run
      it from the Create League page.
    </>
  ),
  // The int-only payload cannot carry the requester's name — no user resolver exists.
  // The link goes to the teamownership page, which lists who asked and their comment.
  'teamRequest.received': (p, l) => (
    <>Someone has requested to take over the {l.team(p.teamId)}.</>
  ),
  'teamRequest.accepted': (p, l) => (
    <>Your request to take over the {l.team(p.teamId)} was accepted.</>
  ),
  'teamRequest.declined': (p, l) => (
    <>Your request to take over the {l.team(p.teamId)} was declined.</>
  ),
}

export function renderNotificationText(
  item: NotificationItem,
  lookup: NotificationLookup,
): ReactNode {
  const renderer = item.type ? RENDERERS[item.type] : undefined
  if (renderer) return renderer(item.payload ?? {}, lookup)
  // Safe generic fallback for any unregistered or null type.
  return <>You have a new notification.</>
}
